import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BasicCli from "../../main/components/basicCli/BasicCli";
import { ApiConstants } from "../../main/constants/ApiConstants";
import axiosInstance from "../../main/initializers/axios";
import GeneralModal from "../../main/components/modal/general/GeneralModal";
import "./Currencies.css";

type CurrencyRow = {
  id: number;
  name: string;
  code: string;
  symbol?: string | null;
  value: number;
  isBase: boolean;
  isActive: boolean;
};

type LoadResponse = {
  insertedOrUpdated?: number;
};

type CurrencyEditPayload = {
  id: number;
  name: string;
  symbol?: string | null;
  value: number;
  isActive: boolean;
};

export default function Currencies() {
  const { i18n, t } = useTranslation();
  const [rows, setRows] = useState<CurrencyRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [loadedCount, setLoadedCount] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editModel, setEditModel] = useState<CurrencyEditPayload | null>(null);
  const [saving, setSaving] = useState(false);
  const [showLoadConfirmation, setShowLoadConfirmation] = useState(false);

  const fetchRows = useCallback(async () => {
    setRefreshing(true);
    setError("");
    try {
      const res = await axiosInstance.get<CurrencyRow[]>(
        ApiConstants.Currencies.list,
      );
      setRows(res.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? i18n.t("errors.generalError"));
    } finally {
      setRefreshing(false);
    }
  }, [i18n]);

  const loadCurrencies = async () => {
    setShowLoadConfirmation(false);
    setLoading(true);
    setError("");
    setLoadedCount(null);
    try {
      const res = await axiosInstance.post<LoadResponse>(
        ApiConstants.Currencies.load,
      );
      const count = res.data?.insertedOrUpdated ?? 0;
      setLoadedCount(count);
      await fetchRows();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? i18n.t("errors.generalError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const startEdit = (row: CurrencyRow) => {
    setError("");
    setEditingId(row.id);
    setEditModel({
      id: row.id,
      name: row.name,
      symbol: row.symbol ?? "",
      value: Number(row.value ?? 0),
      isActive: row.isActive,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditModel(null);
  };

  const saveEdit = async () => {
    if (!editModel) return;

    setSaving(true);
    setError("");
    try {
      await axiosInstance.post(ApiConstants.Currencies.update, editModel);
      cancelEdit();
      await fetchRows();
    } catch (e: any) {
      if (e?.response?.status === 404) {
        setError(
          i18n.t("currencies.updateEndpointMissing", {
            defaultValue:
              "Update endpoint not found. Restart/rebuild backend and ensure /api/currencies/update is deployed.",
          }),
        );
        return;
      }
      setError(e?.response?.data?.message ?? i18n.t("errors.generalError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <BasicCli>
      <div className="curWrap bcPageContent">
        <div className="curContainer">
          <div className="bcPageHeader">
            <div>
              <h1 className="bcPageTitle">
                {i18n.t("currencies.title", { defaultValue: "Currencies" })}
              </h1>
              <p className="curSubtitle">
                {i18n.t("currencies.subtitle", {
                  defaultValue: "Manage and refresh company exchange rates",
                })}
              </p>
            </div>

            <div className="bcPageHeaderActions">
              <button
                className="ui-control ui-btn ui-btn--primary"
                onClick={() => setShowLoadConfirmation(true)}
                disabled={loading}
              >
                {loading
                  ? i18n.t("currencies.loading", { defaultValue: "Loading..." })
                  : i18n.t("currencies.load", {
                      defaultValue: "Load currencies",
                    })}
              </button>
              <button
                className="ui-control ui-btn"
                onClick={fetchRows}
                disabled={refreshing || loading}
              >
                {i18n.t("currencies.refresh", { defaultValue: "Refresh" })}
              </button>
            </div>
          </div>

          {loadedCount !== null && (
            <div className="curInfo">
              {t("currencies.loadedCount", {
                defaultValue: "Loaded {{count}} currencies.",
                count: loadedCount,
              })}
            </div>
          )}
          {error && <div className="curError">{error}</div>}

          <div className="bcCard curTableWrap">
            <table className="curTable">
              <thead>
                <tr>
                  <th>{i18n.t("generic.code")}</th>
                  <th>{i18n.t("generic.name")}</th>
                  <th>
                    {i18n.t("currencies.symbol", { defaultValue: "Symbol" })}
                  </th>
                  <th className="isRight">
                    {i18n.t("currencies.valueAll", {
                      defaultValue: "Value (ALL)",
                    })}
                  </th>
                  <th>{i18n.t("currencies.base", { defaultValue: "Base" })}</th>
                  <th>
                    {i18n.t("actions.actions", { defaultValue: "Actions" })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>{r.code}</td>
                    <td>
                      {editingId === r.id ? (
                        <input
                          className="curInput"
                          value={editModel?.name ?? ""}
                          onChange={(e) =>
                            setEditModel((prev) =>
                              prev ? { ...prev, name: e.target.value } : prev,
                            )
                          }
                        />
                      ) : (
                        r.name
                      )}
                    </td>
                    <td>
                      {editingId === r.id ? (
                        <input
                          className="curInput"
                          value={editModel?.symbol ?? ""}
                          onChange={(e) =>
                            setEditModel((prev) =>
                              prev ? { ...prev, symbol: e.target.value } : prev,
                            )
                          }
                        />
                      ) : (
                        (r.symbol ?? "")
                      )}
                    </td>
                    <td className="isRight">
                      {editingId === r.id ? (
                        <input
                          className="curInput isNumber"
                          type="number"
                          step="0.000001"
                          min={r.isBase ? 1 : 0}
                          disabled={r.isBase}
                          value={editModel?.value ?? 0}
                          onChange={(e) =>
                            setEditModel((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    value: Number(e.target.value || 0),
                                  }
                                : prev,
                            )
                          }
                        />
                      ) : (
                        Number(r.value).toFixed(6)
                      )}
                    </td>
                    <td>
                      {r.isBase
                        ? i18n.t("currencies.yes", { defaultValue: "Yes" })
                        : ""}
                    </td>
                    <td className="curActions">
                      {editingId === r.id ? (
                        <>
                          <button
                            className="curActionBtn isSave"
                            onClick={saveEdit}
                            disabled={saving}
                          >
                            {i18n.t("generic.save")}
                          </button>
                          <button
                            className="curActionBtn"
                            onClick={cancelEdit}
                            disabled={saving}
                          >
                            {i18n.t("actions.cancel", {
                              defaultValue: "Cancel",
                            })}
                          </button>
                        </>
                      ) : (
                        <button
                          className="curActionBtn"
                          onClick={() => startEdit(r)}
                          disabled={saving || loading || refreshing}
                        >
                          {i18n.t("actions.edit", { defaultValue: "Edit" })}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && !refreshing && (
                  <tr>
                    <td className="curEmpty" colSpan={6}>
                      {i18n.t("generic.noData")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {showLoadConfirmation && (
          <GeneralModal
            headerName={i18n.t("currencies.restoreTitle", {
              defaultValue: "Restore currency values?",
            })}
            variant="warning"
            width="480px"
            radius="14px"
            handleClose={() => setShowLoadConfirmation(false)}
            handleClick={loadCurrencies}
            cancelText={i18n.t("actions.cancel", {
              defaultValue: "Cancel",
            })}
            confirmText={i18n.t("currencies.restoreConfirm", {
              defaultValue: "Restore values",
            })}
          >
            <p className="curConfirmText">
              {i18n.t("currencies.restoreMessage", {
                defaultValue:
                  "Do you want to restore the default currency values? Any manual changes will be replaced.",
              })}
            </p>
          </GeneralModal>
        )}
      </div>
    </BasicCli>
  );
}
